/**
 * Smart Music Recommendations Hook
 * Uses AI to generate personalized music recommendations
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

export interface LocationContext {
  city?: string;
  neighborhood?: string;
  state?: string;
  country?: string;
  placeType?: 'beach' | 'park' | 'urban' | 'trail' | 'gym' | 'residential' | 'stadium' | 'unknown';
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface RecommendationContext {
  bpm?: number;
  mood?: string;
  timeOfDay?: string;
  weather?: string;
  location?: LocationContext;
}

interface MusicRecommendation {
  track: string;
  artist: string;
  bpm: number;
  energy: number;
  genre: string;
  reason: string;
  discovery: boolean;
}

interface MusicInsights {
  preferred_bpm_range: string;
  favorite_genres: string[];
  energy_preference: string;
  listening_patterns: string;
}

interface RecommendationResponse {
  recommendations: MusicRecommendation[];
  insights: MusicInsights;
}

export function useSmartMusicRecommendations() {
  const [recommendations, setRecommendations] = useState<MusicRecommendation[]>([]);
  const [insights, setInsights] = useState<MusicInsights | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const generateRecommendations = useCallback(async (context: RecommendationContext) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-smart-music-recommendations', {
        body: { context }
      });

      if (error) {
        if (error.message.includes('Rate limit')) {
          toast.error('Muitas requisições. Tente novamente em alguns minutos.');
        } else if (error.message.includes('Payment required')) {
          toast.error('Créditos esgotados. Entre em contato com o suporte.');
        } else {
          toast.error('Erro ao gerar recomendações');
        }
        throw error;
      }

      const response = data as RecommendationResponse;
      setRecommendations(response.recommendations);
      setInsights(response.insights);

      logger.info('Smart recommendations generated', {
        count: response.recommendations.length,
        context,
      });

      return response;
    } catch (error) {
      logger.error('Failed to generate recommendations', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveFeedback = useCallback(async (
    track: string,
    artist: string,
    feedback: 'like' | 'dislike' | 'skip' | 'repeat',
    context?: RecommendationContext
  ) => {
    try {
      const { error } = await supabase.functions.invoke('save-music-feedback', {
        body: { track, artist, feedback, context }
      });

      if (error) throw error;

      logger.info('Music feedback saved', { track, artist, feedback });
    } catch (error) {
      logger.error('Failed to save music feedback', error);
    }
  }, []);

  return {
    recommendations,
    insights,
    isLoading,
    generateRecommendations,
    saveFeedback,
  };
}
